import {
  Button,
  DocViewerComponent,
  Icons,
  useToggle,
} from '@commenda/kakashi';
import { ServiceRequestDetails } from '@commenda/types/src/service-request/types';
import { documentName } from '@commenda/utils';
import { getSignedUrlByFileId } from '@commenda/utils/files/uploadFile';
import { LegacyDocumentType } from '@prisma/client';
import { useEffect, useState } from 'react';
import { KeyedMutator } from 'swr';
import DirectorUploadFileModal from '../../../common/modals/DirectorUploadFileModal';
import { useServiceRequestFile } from '../../../hook/useServiceRequestFile';

const DirectorDocumentUploadSection = ({
  documentType,
  directorId,
  onUploadSuccess,
  serviceRequest,
  mutate,
  serviceRequestTaskId,
}: {
  documentType: LegacyDocumentType;
  directorId: number;
  onUploadSuccess?: () => void;
  serviceRequest: ServiceRequestDetails;
  mutate: KeyedMutator<unknown>;
  serviceRequestTaskId: number;
}) => {
  const [file, setFile] = useServiceRequestFile(serviceRequest, documentType);
  const [documentLink, setDocumentLink] = useState('');

  const uploadModalVisibility = useToggle();

  const currentDirector =
    serviceRequest?.corporation?.corporationHasDirector?.find(
      (each) => each?.director?.id === directorId
    );

  const uploadedDocument = currentDirector?.documents?.find(
    (each) => each?.documentType === documentType
  );

  const uploadedFile = uploadedDocument?.files?.sort((a, b) => b.id - a.id)[0];

  useEffect(() => {
    let mounted = true;

    const fetchSignedUrl = async () => {
      try {
        if (uploadedFile?.id) {
          const link = await getSignedUrlByFileId(uploadedFile.id, null);
          if (mounted) {
            setDocumentLink(link);
          }
        }
      } catch (error) {
        console.error('Error fetching signed URL:', error);
      }
    };

    fetchSignedUrl();

    return () => {
      mounted = false;
    };
  }, [serviceRequest, uploadedDocument]);

  return (
    <div className="flex flex-col h-full">
      <div className="flex w-full items-center justify-end mb-4">
        <Button
          variant={uploadedDocument ? 'secondary' : 'primary'}
          onClick={uploadModalVisibility.toggle}
          size="sm"
        >
          <Icons.Upload01 className="w-4 h-4" />
          {uploadedDocument ? 'Reupload' : 'Upload'}
        </Button>
      </div>
      <DirectorUploadFileModal
        serviceRequestTaskId={serviceRequestTaskId}
        serviceRequest={serviceRequest}
        file={file}
        directorId={directorId}
        setFile={setFile}
        isOpen={uploadModalVisibility.visible}
        setIsOpen={uploadModalVisibility.hide}
        onUploadSuccess={async () => {
          if (onUploadSuccess) onUploadSuccess();
          await mutate();
        }}
        documentType={documentType}
        heading={`Upload ${documentName(documentType)}`}
      />
      {uploadedDocument ? (
        <DocViewerComponent uri={documentLink} />
      ) : (
        <div className="rounded-2xl bg-bg-secondary h-full mt-6 flex flex-col items-center justify-center">
          <div className="flex h-12 w-12 rounded-lg bg-white border border-primary items-center justify-center mb-5">
            <Icons.File05 className="h-5 w-5" />
          </div>
          <div className="mt-4 flex flex-col gap-1">
            <h3 className="txt-medium-bold text-center text-text-primary">
              No document uploaded
            </h3>
            <p className="text-center">
              Once a document has been uploaded, a preview will load here.
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

export default DirectorDocumentUploadSection;
